/**
 * RetentionPromiseSection — the "5-year hook" pitch on the landing page.
 *
 * Sits between the Features Bento and Discovery Feed. Every line is a
 * retention pillar that answers the question students don't quite know
 * how to phrase: "what happens to my study work after this semester?"
 *
 * Four cards, all clickable to /login (auth gate). Klein-blue, F500-styled.
 */
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, GraduationCap, Target, Heart, ArrowRight } from 'lucide-react';

const PILLARS = [
  {
    icon: Brain,
    eyebrow: 'A TUTOR THAT KNOWS YOU',
    title: "Tell it you're premed — once.",
    body:
      "Every chat after that knows your major, your weak topics, the date of your finals. So when you ask a question, the answer already fits the course you're actually taking.",
    accent: '#002FA7',
  },
  {
    icon: GraduationCap,
    eyebrow: 'YOUR 4-YEAR TRANSCRIPT',
    title: 'Your study work compounds.',
    body:
      "Every lecture, every flashcard, every wrong answer streams into one growing transcript. By the time you're a senior, you can point to it and say 'this is what I know, and this is what I'm about to forget.'",
    accent: '#0066FF',
  },
  {
    icon: Target,
    eyebrow: 'MISTAKES BECOME TOMORROW',
    title: 'Your deck grows from weakness, not work.',
    body:
      "Get a quiz answer wrong? It's queued as a flashcard for tomorrow morning. You stop practicing what you already know and start practicing what you're about to forget.",
    accent: '#0040D0',
  },
  {
    icon: Heart,
    eyebrow: 'STUDY PACTS · MUTUAL STREAKS',
    title: "Show up for them, they show up for you.",
    body:
      "Pair with one friend on a 14-day pact. If either of you misses a day, both get a nudge. Studying with someone you'd hate to let down is the single biggest predictor of actually finishing the term.",
    accent: '#001F70',
  },
];

export default function RetentionPromiseSection() {
  return (
    <section
      className="py-20 px-4 sm:px-6 lg:px-12"
      style={{ backgroundColor: 'var(--isp-page)' }}
      data-testid="retention-promise-section"
    >
      <div className="max-w-6xl mx-auto">
        <p
          className="text-xs font-semibold uppercase tracking-[0.2em] text-[#002FA7] mb-3"
          style={{ fontFamily: 'var(--sf-text)' }}
        >
          Why students stay
        </p>
        <h2
          className="text-3xl sm:text-4xl font-bold text-[#0A0A0A] tracking-tight mb-2 max-w-3xl"
          style={{ fontFamily: 'var(--sf-display)' }}
        >
          Most study apps you delete after one exam.
        </h2>
        <p
          className="text-lg text-[#52525B] mb-12 max-w-2xl"
          style={{ fontFamily: 'var(--sf-text)' }}
        >
          iStudyPro is engineered to be the one you open at 7pm on a Tuesday
          for four straight years — because every day you show up, the next
          day gets easier.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E4E4E7]">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                className="bg-[var(--isp-page)] p-8 lg:p-10 hover:bg-[#FAFAFA] transition-colors"
                data-testid={`retention-pillar-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${p.accent}10` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: p.accent }} />
                  </div>
                  <div className="min-w-0">
                    <p
                      className="text-[10px] font-semibold uppercase tracking-[0.18em] mb-2"
                      style={{ color: p.accent, fontFamily: 'var(--sf-text)' }}
                    >
                      {p.eyebrow}
                    </p>
                    <h3
                      className="text-lg font-bold text-[#0A0A0A] tracking-tight mb-2"
                      style={{ fontFamily: 'var(--sf-display)' }}
                    >
                      {p.title}
                    </h3>
                    <p
                      className="text-sm text-[#52525B] leading-relaxed"
                      style={{ fontFamily: 'var(--sf-text)' }}
                    >
                      {p.body}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold text-sm tracking-tight transition-colors"
            style={{ fontFamily: 'var(--sf-text)' }}
            data-testid="retention-promise-cta"
          >
            Start your 7-day habit · Earn a free week of Pro
            <ArrowRight className="w-4 h-4" />
          </Link>
          <p
            className="text-xs text-[#A1A1AA]"
            style={{ fontFamily: 'var(--sf-text)' }}
          >
            No card required · Your data exports any time · Built in Ghana for the world
          </p>
        </div>
      </div>
    </section>
  );
}
